import {
  ArrowPathIcon,
  ArrowUpTrayIcon,
  DocumentPlusIcon,
  FolderIcon,
  FolderPlusIcon,
  Squares2X2Icon,
  ListBulletIcon,
} from '@heroicons/react/24/outline';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Spinner,
  Textarea,
  ButtonGroup,
} from '@nextui-org/react';
import axios from 'axios';
import {
  FullFileBrowser,
  ChonkyActions,
  FileArray,
  FileData,
  setChonkyDefaults,
  ChonkyFileActionData,
  defineFileAction,
  ChonkyIconName,
} from 'chonky';
import { ChonkyIconFA } from 'chonky-icon-fontawesome';
import React, { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import env from 'react-dotenv';
import { toast } from 'react-toastify';

import { FrontendServerSchema, SftpFileItem } from '@common/types';
import { ModernModal } from '@portal/components/common/ModernModal.ui';
import { tsrestClient } from '@portal/provider/tsrest';

// Lazy load the text editor (Monaco Editor is heavy)
const SftpTextEditor = lazy(() => import('./SftpTextEditor').then(module => ({ default: module.SftpTextEditor })));

// Set default icons for Chonky
// eslint-disable-next-line @typescript-eslint/no-explicit-any
setChonkyDefaults({ iconComponent: ChonkyIconFA as any });

interface SftpBrowserProps {
  isOpen: boolean;
  onClose: () => void;
  server: FrontendServerSchema | null;
}

type ViewMode = 'grid' | 'list';

export const SftpBrowser: React.FC<SftpBrowserProps> = ({ isOpen, onClose, server }) => {
  const [currentPath, setCurrentPath] = useState('/root');
  const [files, setFiles] = useState<FileArray>([]);
  const [folderChain, setFolderChain] = useState<FileData[]>([]);
  const [lastRefreshTime, setLastRefreshTime] = useState<Date | null>(null);
  const [, setTick] = useState(0); // Force re-render for time updates
  const [viewMode, setViewMode] = useState<ViewMode>('grid');

  // Modal states
  const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [isCreateFileModalOpen, setIsCreateFileModalOpen] = useState(false);
  const [newFileName, setNewFileName] = useState('');
  const [newFileContent, setNewFileContent] = useState('');
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [renamingFile, setRenamingFile] = useState<{ oldPath: string; oldName: string } | null>(null);
  const [newName, setNewName] = useState('');
  const [isTextEditorOpen, setIsTextEditorOpen] = useState(false);
  const [editingFile, setEditingFile] = useState<{ path: string; name: string } | null>(null);

  // Reset path when server changes or modal opens
  useEffect(() => {
    if (isOpen && server) {
      setCurrentPath('/root');
      setFolderChain([]);
    }
  }, [server?.uuid, isOpen]);

  // Query for directory listing
  const {
    data: listData,
    isLoading: isLoadingList,
    refetch,
  } = tsrestClient.sftp.list.useQuery(
    ['sftp-list', server?.uuid, currentPath],
    {
      params: { uuid: server?.uuid || '' },
      query: { path: currentPath },
      headers: {
        authorization: localStorage.getItem('token') || '',
      },
    },
    {
      enabled: isOpen && !!server,
    }
  );

  // Update files when data changes
  useEffect(() => {
    if (listData?.status === 200) {
      const items = listData.body.items;

      // Convert to Chonky format
      const chonkyFiles: FileArray = items.map((item: SftpFileItem) => ({
        id: item.path,
        name: item.name,
        isDir: item.type === 'directory',
        size: item.size,
        modDate: new Date(item.modifiedDate),
      }));

      setFiles(chonkyFiles);

      // Build folder chain for breadcrumb
      const pathParts = currentPath.split('/').filter(p => p);
      const chain: FileData[] = [{ id: '/', name: 'Root', isDir: true }];

      let currentChainPath = '';
      pathParts.forEach(part => {
        currentChainPath += `/${part}`;
        chain.push({
          id: currentChainPath,
          name: part,
          isDir: true,
        });
      });

      setFolderChain(chain);
      setLastRefreshTime(new Date()); // Update last refresh time
    }
  }, [listData, currentPath]);

  // Update timestamp display every second
  useEffect(() => {
    if (!isOpen || !lastRefreshTime) return;

    const interval = setInterval(() => {
      setTick(prev => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen, lastRefreshTime]);

  // Mutations
  const deleteMutation = tsrestClient.sftp.delete.useMutation();
  const createDirectoryMutation = tsrestClient.sftp.createDirectory.useMutation();
  const writeFileMutation = tsrestClient.sftp.writeFile.useMutation();
  const renameMutation = tsrestClient.sftp.rename.useMutation();

  // Handle file actions
  const handleFileAction = useCallback(
    async (data: ChonkyFileActionData) => {
      if (!server) return;

      if (data.id === ChonkyActions.OpenFiles.id) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const targetFile = (data.payload as any).targetFile;

        if (targetFile && targetFile.isDir) {
          // Navigate into directory
          setCurrentPath(targetFile.id);
        } else if (targetFile && !targetFile.isDir) {
          // Open text editor for file
          setEditingFile({ path: targetFile.id, name: targetFile.name });
          setIsTextEditorOpen(true);
        }
      } else if (data.id === ChonkyActions.UploadFiles.id) {
        // Handle file upload from Chonky
        setIsUploadModalOpen(true);
      } else if ((data.id as string) === 'rename_file') {
        // Handle rename - custom action
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const selectedFiles = (data as any).state?.selectedFiles || [];
        if (selectedFiles.length === 1) {
          const file = selectedFiles[0];
          setRenamingFile({ oldPath: file.id, oldName: file.name });
          setNewName(file.name);
          setIsRenameModalOpen(true);
        } else if (selectedFiles.length === 0) {
          toast.warning('Please select a file or folder to rename');
        } else {
          toast.warning('Please select only one file or folder to rename');
        }
      } else if (data.id === ChonkyActions.DeleteFiles.id) {
        // Delete selected files
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const files = (data as any).state?.selectedFiles || [];
        if (files.length === 0) return;

        const confirmDelete = window.confirm(`Delete ${files.length} file(s)?`);
        if (!confirmDelete) return;

        for (const file of files) {
          try {
            await deleteMutation.mutateAsync({
              params: { uuid: server.uuid },
              body: { path: file.id, isDirectory: file.isDir || false },
              headers: {
                authorization: localStorage.getItem('token') || '',
              },
            });
          } catch (error) {
            toast.error(`Failed to delete ${file.name}`);
          }
        }

        toast.success('Deleted successfully');
        refetch();
      } else if (data.id === ChonkyActions.DownloadFiles.id) {
        // Download selected files
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const files = (data as any).state?.selectedFiles || [];
        if (files.length === 0) {
          toast.warning('Please select files to download');
          return;
        }

        for (const file of files) {
          if (file.isDir) {
            toast.info('Directory download not supported yet');
            continue;
          }

          try {
            // Use axios for file download (not in ts-rest contract)
            const baseUrl = env.API_URL || '';
            const response = await axios.get(`${baseUrl}/api/v1/sftp/${server.uuid}/download`, {
              params: { path: file.id },
              headers: {
                authorization: localStorage.getItem('token') || '',
              },
              responseType: 'blob',
            });

            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = file.name;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            toast.success(`Downloaded ${file.name}`);
          } catch (error) {
            toast.error(`Failed to download ${file.name}`);
          }
        }
      } else if (data.id === ChonkyActions.EnableGridView.id) {
        setViewMode('grid');
      } else if (data.id === ChonkyActions.EnableListView.id) {
        setViewMode('list');
      }
    },
    [server, deleteMutation, refetch]
  );

  // Custom actions - define custom rename action
  const customRenameAction = useMemo(
    () =>
      defineFileAction({
        id: 'rename_file',
        requiresSelection: true,
        button: {
          name: 'Rename',
          toolbar: true,
          contextMenu: true,
          group: 'Actions',
          icon: ChonkyIconName.config,
        },
      }),
    []
  );

  const fileActions = useMemo(
    () => [
      ChonkyActions.UploadFiles,
      ChonkyActions.DownloadFiles,
      ChonkyActions.DeleteFiles,
      customRenameAction,
      ChonkyActions.EnableGridView,
      ChonkyActions.EnableListView,
    ],
    [customRenameAction]
  );

  // Handle create folder
  const handleCreateFolder = async () => {
    if (!server || !newFolderName.trim()) return;

    try {
      const newPath = `${currentPath}/${newFolderName}`.replace('//', '/');
      await createDirectoryMutation.mutateAsync({
        params: { uuid: server.uuid },
        body: { path: newPath },
        headers: {
          authorization: localStorage.getItem('token') || '',
        },
      });

      toast.success('Folder created successfully');
      setIsCreateFolderModalOpen(false);
      setNewFolderName('');
      refetch();
    } catch (error) {
      toast.error('Failed to create folder');
    }
  };

  // Handle create file
  const handleCreateFile = async () => {
    if (!server || !newFileName.trim()) return;

    try {
      const newPath = `${currentPath}/${newFileName}`.replace('//', '/');
      await writeFileMutation.mutateAsync({
        params: { uuid: server.uuid },
        body: { path: newPath, content: newFileContent },
        headers: {
          authorization: localStorage.getItem('token') || '',
        },
      });

      toast.success('File created successfully');
      setIsCreateFileModalOpen(false);
      setNewFileName('');
      setNewFileContent('');
      refetch();
    } catch (error) {
      toast.error('Failed to create file');
    }
  };

  // Handle rename file/folder
  const handleRename = async () => {
    if (!server || !renamingFile || !newName.trim()) return;

    const oldPath = renamingFile.oldPath;
    const dirPath = oldPath.substring(0, oldPath.lastIndexOf('/'));
    const newPath = dirPath ? `${dirPath}/${newName}` : `/${newName}`;

    try {
      await renameMutation.mutateAsync({
        params: { uuid: server.uuid },
        body: { oldPath, newPath },
        headers: {
          authorization: localStorage.getItem('token') || '',
        },
      });

      toast.success(`Renamed '${renamingFile.oldName}' to '${newName}' successfully`);
      setIsRenameModalOpen(false);
      setRenamingFile(null);
      setNewName('');
      refetch();
    } catch (error) {
      toast.error(`Failed to rename '${renamingFile.oldName}'`);
    }
  };

  // Handle file upload
  const handleFileUpload = async () => {
    if (!server || !selectedFiles || selectedFiles.length === 0) return;

    try {
      const formData = new FormData();
      formData.append('targetPath', currentPath);

      for (let i = 0; i < selectedFiles.length; i++) {
        formData.append('files', selectedFiles[i]);
      }

      // Use direct fetch for FormData upload (not in ts-rest contract)
      const baseUrl = env.API_URL || '';
      const response = await fetch(`${baseUrl}/api/v1/sftp/${server.uuid}/upload`, {
        method: 'POST',
        headers: {
          authorization: localStorage.getItem('token') || '',
        },
        body: formData,
      });

      const result = await response.json();

      if (response.ok) {
        toast.success(result.message);
        setIsUploadModalOpen(false);
        setSelectedFiles(null);
        refetch();
      } else {
        toast.error(`Failed to upload: ${result.message}`);
      }
    } catch (error) {
      toast.error('Failed to upload files');
    }
  };

  const handleRefresh = () => {
    refetch();
  };

  const formatLastRefreshTime = () => {
    if (!lastRefreshTime) return '';
    const now = new Date();
    const diff = now.getTime() - lastRefreshTime.getTime();
    const seconds = Math.floor(diff / 1000);

    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  };

  return (
    <>
      <ModernModal
        isOpen={isOpen}
        onOpenChange={onClose}
        title='File Manager (SFTP)'
        subtitle={
          <div className='flex items-center gap-2'>
            <span>{server ? `${server.name} - ${currentPath}` : 'Loading...'}</span>
            {lastRefreshTime && <span className='text-xs opacity-70'>• Last refresh: {formatLastRefreshTime()}</span>}
          </div>
        }
        icon={<FolderIcon className='h-4 w-4' />}
        size='5xl'
        customHeaderActions={
          <div className='flex gap-2'>
            {/* View Mode Selector - styled like pagination */}
            <ButtonGroup size='sm' className='mr-2'>
              <Button
                variant={viewMode === 'grid' ? 'solid' : 'bordered'}
                color={viewMode === 'grid' ? 'primary' : 'default'}
                isIconOnly
                onPress={() => setViewMode('grid')}
                title='Grid View'
                className={viewMode === 'grid' ? '' : 'bg-white/10 text-white border-white/20'}
              >
                <Squares2X2Icon className='w-4 h-4' />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'solid' : 'bordered'}
                color={viewMode === 'list' ? 'primary' : 'default'}
                isIconOnly
                onPress={() => setViewMode('list')}
                title='List View'
                className={viewMode === 'list' ? '' : 'bg-white/10 text-white border-white/20'}
              >
                <ListBulletIcon className='w-4 h-4' />
              </Button>
            </ButtonGroup>
            <Button
              size='sm'
              variant='flat'
              isIconOnly
              className='bg-white/20 hover:bg-white/30 text-white'
              onPress={handleRefresh}
              isLoading={isLoadingList}
            >
              <ArrowPathIcon className='w-4 h-4' />
            </Button>
            <Button
              size='sm'
              variant='flat'
              className='bg-white/20 hover:bg-white/30 text-white'
              startContent={<DocumentPlusIcon className='w-4 h-4' />}
              onPress={() => setIsCreateFileModalOpen(true)}
            >
              New File
            </Button>
            <Button
              size='sm'
              variant='flat'
              className='bg-white/20 hover:bg-white/30 text-white'
              startContent={<FolderPlusIcon className='w-4 h-4' />}
              onPress={() => setIsCreateFolderModalOpen(true)}
            >
              New Folder
            </Button>
            <Button
              size='sm'
              className='bg-white hover:bg-white/90 text-blue-600'
              startContent={<ArrowUpTrayIcon className='w-4 h-4' />}
              onPress={() => setIsUploadModalOpen(true)}
            >
              Upload
            </Button>
          </div>
        }
      >
        {isLoadingList ? (
          <div className='flex items-center justify-center h-96'>
            <Spinner size='lg' />
          </div>
        ) : (
          <div style={{ height: '600px' }}>
            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/* @ts-ignore - Chonky v2 uses React 17 types, compatible with React 18 at runtime */}
            <FullFileBrowser
              files={files}
              folderChain={folderChain}
              fileActions={fileActions}
              onFileAction={handleFileAction}
              disableDefaultFileActions={true}
              defaultFileViewActionId={
                viewMode === 'grid' ? ChonkyActions.EnableGridView.id : ChonkyActions.EnableListView.id
              }
            />
          </div>
        )}
      </ModernModal>

      {/* Create File Modal */}
      <Modal isOpen={isCreateFileModalOpen} onClose={() => setIsCreateFileModalOpen(false)} size='xl'>
        <ModalContent>
          <ModalHeader>Create New File</ModalHeader>
          <ModalBody>
            <Input
              label='File Name'
              placeholder='Enter file name (e.g., script.sh, config.txt)'
              value={newFileName}
              onChange={e => setNewFileName(e.target.value)}
              autoFocus
            />
            <Textarea
              label='File Content (Optional)'
              placeholder='Enter file content...'
              value={newFileContent}
              onValueChange={setNewFileContent}
              minRows={5}
              maxRows={15}
            />
          </ModalBody>
          <ModalFooter>
            <Button
              variant='light'
              onPress={() => {
                setIsCreateFileModalOpen(false);
                setNewFileName('');
                setNewFileContent('');
              }}
            >
              Cancel
            </Button>
            <Button color='primary' onPress={handleCreateFile} isLoading={writeFileMutation.isLoading}>
              Create File
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Create Folder Modal */}
      <Modal isOpen={isCreateFolderModalOpen} onClose={() => setIsCreateFolderModalOpen(false)}>
        <ModalContent>
          <ModalHeader>Create New Folder</ModalHeader>
          <ModalBody>
            <Input
              label='Folder Name'
              placeholder='Enter folder name'
              value={newFolderName}
              onChange={e => setNewFolderName(e.target.value)}
              autoFocus
            />
          </ModalBody>
          <ModalFooter>
            <Button
              variant='light'
              onPress={() => {
                setIsCreateFolderModalOpen(false);
                setNewFolderName('');
              }}
            >
              Cancel
            </Button>
            <Button color='primary' onPress={handleCreateFolder} isLoading={createDirectoryMutation.isLoading}>
              Create Folder
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Upload Modal */}
      <Modal isOpen={isUploadModalOpen} onClose={() => setIsUploadModalOpen(false)}>
        <ModalContent>
          <ModalHeader>Upload Files</ModalHeader>
          <ModalBody>
            <input type='file' multiple onChange={e => setSelectedFiles(e.target.files)} className='w-full' />
            {selectedFiles && selectedFiles.length > 0 && (
              <p className='text-sm text-gray-500 mt-2'>{selectedFiles.length} file(s) selected</p>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              variant='light'
              onPress={() => {
                setIsUploadModalOpen(false);
                setSelectedFiles(null);
              }}
            >
              Cancel
            </Button>
            <Button
              color='primary'
              onPress={handleFileUpload}
              isDisabled={!selectedFiles || selectedFiles.length === 0}
            >
              Upload Files
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Rename Modal */}
      <Modal isOpen={isRenameModalOpen} onClose={() => setIsRenameModalOpen(false)}>
        <ModalContent>
          <ModalHeader>Rename &apos;{renamingFile?.oldName}&apos;</ModalHeader>
          <ModalBody>
            <Input
              label='New Name'
              placeholder='Enter new name'
              value={newName}
              onChange={e => setNewName(e.target.value)}
              autoFocus
            />
          </ModalBody>
          <ModalFooter>
            <Button
              variant='light'
              onPress={() => {
                setIsRenameModalOpen(false);
                setRenamingFile(null);
                setNewName('');
              }}
            >
              Cancel
            </Button>
            <Button color='primary' onPress={handleRename} isLoading={renameMutation.isLoading}>
              Rename
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Text Editor Modal - Lazy Loaded */}
      {editingFile && (
        <Suspense
          fallback={
            <Modal isOpen={isTextEditorOpen} size='5xl'>
              <ModalContent>
                <ModalBody>
                  <div className='flex items-center justify-center h-96'>
                    <Spinner size='lg' />
                    <p className='ml-4'>Loading editor...</p>
                  </div>
                </ModalBody>
              </ModalContent>
            </Modal>
          }
        >
          <SftpTextEditor
            isOpen={isTextEditorOpen}
            onClose={() => {
              setIsTextEditorOpen(false);
              setEditingFile(null);
              refetch(); // Refresh directory after editing
            }}
            server={server}
            filePath={editingFile.path}
            fileName={editingFile.name}
          />
        </Suspense>
      )}
    </>
  );
};
